import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy, Eye, EyeOff, Loader2, AlertCircle, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [registrationSettings, setRegistrationSettings] = useState(null);
    const [verificationRequired, setVerificationRequired] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    // Fetch registration settings on mount
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/auth/registration-settings`);
                setRegistrationSettings(res.data);
            } catch (err) {
                console.error('Failed to fetch registration settings:', err);
            }
        };
        fetchSettings();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        // Strong password validation
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        if (!/[A-Z]/.test(password)) {
            toast.error('Password must contain at least one uppercase letter');
            return;
        }
        if (!/[a-z]/.test(password)) {
            toast.error('Password must contain at least one lowercase letter');
            return;
        }
        if (!/[0-9]/.test(password)) {
            toast.error('Password must contain at least one number');
            return;
        }
        if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
            toast.error('Password must contain at least one special character');
            return;
        }

        // Check domain restriction on frontend too
        if (registrationSettings?.domain_restriction_enabled) {
            const emailDomain = email.split('@')[1]?.toLowerCase();
            if (!registrationSettings.allowed_domains.includes(emailDomain)) {
                toast.error(`Registration is restricted to: ${registrationSettings.allowed_domains.join(', ')}`);
                return;
            }
        }

        setLoading(true);
        
        try {
            const result = await register(email, password, name);
            
            // Check if verification is required
            if (result?.message?.includes('verify')) {
                setVerificationRequired(true);
                toast.success('Account created! Please check your email to verify.');
            } else {
                toast.success('Account created! Welcome to RaceWise');
                navigate('/');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    // Show verification message after registration
    if (verificationRequired) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 py-12">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                            <Mail className="h-8 w-8 text-green-500" />
                        </div>
                        <CardTitle>Check Your Email</CardTitle>
                        <CardDescription>
                            We&apos;ve sent a verification link to <strong>{email}</strong>
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Click the link in the email to verify your account and start using RaceWise.
                        </p>
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                Can&apos;t find it? Check your spam folder. The link expires in 24 hours.
                            </AlertDescription>
                        </Alert>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-3">
                        <Link to="/resend-verification" className="w-full">
                            <Button variant="outline" className="w-full">
                                Resend Verification Email
                            </Button>
                        </Link>
                        <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
                            Back to Login
                        </Link>
                    </CardFooter>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <Card>
                    <CardHeader className="text-center">
                        <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
                            <Trophy className="h-8 w-8 text-primary" />
                            <span className="font-heading font-bold text-2xl tracking-wide">RaceWise</span>
                        </Link>
                        <CardTitle className="text-xl font-semibold">Create an account</CardTitle>
                        <CardDescription>Join the tipping competition</CardDescription>
                    </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Full Name</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Your full name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                data-testid="register-name"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                data-testid="register-email"
                            />
                            {registrationSettings?.domain_restriction_enabled && (
                                <p className="text-xs text-amber-500 flex items-center gap-1">
                                    <AlertCircle className="h-3 w-3" />
                                    Restricted to: {registrationSettings.allowed_domains.join(', ')}
                                </p>
                            )}
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="register-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full px-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                            <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3 space-y-1">
                                <p className="font-medium text-foreground/80">Password must contain:</p>
                                <ul className="list-disc list-inside space-y-0.5 ml-1">
                                    <li>At least 8 characters</li>
                                    <li>One uppercase letter (A-Z)</li>
                                    <li>One lowercase letter (a-z)</li>
                                    <li>One number (0-9)</li>
                                    <li>One special character (!@#$%^&* etc.)</li>
                                </ul>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                data-testid="register-confirm-password"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full btn-pill" 
                            disabled={loading}
                            data-testid="register-submit"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                'Create Account'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center text-sm">
                        <span className="text-muted-foreground">Already have an account? </span>
                        <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                            Sign in
                        </Link>
                    </div>

                    <p className="mt-4 text-xs text-center text-muted-foreground">
                        By creating an account, you agree to our Terms of Service and confirm you are 18+ years old.
                    </p>
                </CardContent>
            </Card>
            
            {/* Powered by Podium */}
            <div className="mt-6 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <span className="text-sm">Powered by</span>
                <img 
                    src="https://media.ffycdn.net/eu/pa-media-group/UDcy2NnpYEbTrc3irv2X.png?width=600&format=webp&quality=100" 
                    alt="Podium" 
                    className="h-8 w-auto"
                />
            </div>
            </div>
        </div>
    );
}
